import { Checkbox } from "components/Atoms/inputs/Checkbox";
import FreeFieldCategoryDropdown from "components/dropdowns/reactselect/FreeFieldCategoryDropdown";
import { canModifyFieldType, FreeFieldModifyRadio } from "components/Freefields/FreeFieldTypeRadio";
import ReadonlyPercentage from "components/Freefields/Readonly/ReadonlyPercentage";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import PercentageInput from "components/PercentageInput";
import { FreeFieldReferenceInput } from "components/ValidatedFields/FreeFieldReferenceInput";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import React, { useState } from "react";
import { getFormElementType } from "utilities/freefieldUtils";
import { OptionalTranslationValueInput, RequiredTranslationValueInput, TranslationValueReadonly } from "webform/components/TranslationValueInnerForm";
import { DropdownOptionsDetail, DropdownOptionsInPageEdit } from "./DropdownOptions/DropdownOptions";
import FreeFieldPermissions from "./FreeFieldPermissions/FreeFieldPermissions";
import { FreeFieldDefinitionDetailState, FreeFieldDefinitionNewState } from "./model";

interface FreeFieldDefinitionDetailSectionProps {
  mainArea: server.dto.MainAreas
  freeFieldDefinition: server.dto.FreeFieldDefinition
  freeFieldCategory: server.dto.FreeFieldCategoryForList
  freeFieldProfileWithPermission: server.dto.FreeFieldProfileWithPermission[]
}

export const FreeFieldDefinitionDetailSection = (props: FreeFieldDefinitionDetailSectionProps) => {
  const [freeFieldDefinition, setFreeFieldDefinition] = useState<FreeFieldDefinitionDetailState>({
    category: props.freeFieldCategory,
    type: getFormElementType(props.freeFieldDefinition.type),
    label: props.freeFieldDefinition.title,
    subLabel: props.freeFieldDefinition.description,
    reference: props.freeFieldDefinition.reference.slice(2), // Remove the FF prefix
    hiddenFromItinerary: props.freeFieldDefinition.hideFromItinerary,
    hiddenFromMobileApp: props.freeFieldDefinition.hideFromMobileApp,

    choices: props.freeFieldDefinition.dropdownOptions,
    defaultNumberWithDecimal: getDefaultNumberWithDecimal(props.freeFieldDefinition),
    defaultNumberWithoutDecimal: getDefaultNumberWithoutDecimal(props.freeFieldDefinition),
    defaultText: getDefaultText(props.freeFieldDefinition),
    defaultPercentage: getDefaultPercentage(props.freeFieldDefinition),

    permissions: props.freeFieldProfileWithPermission,
  });

  const onValueChanged = (key: string, value: any) => {
    if (key === "category") {
      setFreeFieldDefinition({ ...freeFieldDefinition, category: value });
    } else if (key === "type") {
      setFreeFieldDefinition({ ...freeFieldDefinition, type: value });
    } else if (key === "label") {
      setFreeFieldDefinition({ ...freeFieldDefinition, label: value });
    } else if (key === "subLabel") {
      setFreeFieldDefinition({ ...freeFieldDefinition, subLabel: value });
    } else if (key === "reference") {
      setFreeFieldDefinition({ ...freeFieldDefinition, reference: value });
    } else if (key === "visibility") {
      setFreeFieldDefinition({ ...freeFieldDefinition, hiddenFromItinerary: value.hiddenFromItinerary, hiddenFromMobileApp: value.hiddenFromMobileApp });
    } else if (key === "defaultNumberWithDecimal") {
      setFreeFieldDefinition({ ...freeFieldDefinition, defaultNumberWithDecimal: value });
    } else if (key === "defaultNumberWithoutDecimal") {
      setFreeFieldDefinition({ ...freeFieldDefinition, defaultNumberWithoutDecimal: value });
    } else if (key === "defaultTextSingleLine" || key === "defaultTextMultiLine") {
      setFreeFieldDefinition({ ...freeFieldDefinition, defaultText: value });
    } else if (key === "options") {
      setFreeFieldDefinition({ ...freeFieldDefinition, choices: value.options, defaultText: value.defaultOption });
    } else if (key === "defaultPercentage") {
      setFreeFieldDefinition({ ...freeFieldDefinition, defaultPercentage: value });
    } else if (key === "permissionProfiles") {
      setFreeFieldDefinition({ ...freeFieldDefinition, permissions: value });
    }
  }

  const getUpdateFreeFieldDefinitionInstruction = (ffc: FreeFieldDefinitionNewState): server.dto.UpdateFreeFieldDefinitionDetailsInstruction => ({
    choices: ffc.choices,
    defaultDecimal: ffc.defaultNumberWithDecimal,
    defaultInteger: ffc.defaultNumberWithoutDecimal,
    defaultText: ffc.defaultText,
    description: ffc.subLabel,
    formElementType: ffc.type,
    freeFieldCategoryId: ffc.category.freeFieldCategoryId,
    freeFieldDefinitionId: props.freeFieldDefinition.freeFieldDefinitionId,
    hideFromItinerary: ffc.hiddenFromItinerary,
    hideFromMobileApp: ffc.hiddenFromMobileApp,
    defaultPercentage: ffc.defaultPercentage,
    reference: ffc.reference,
    title: ffc.label,
    useUntil: null,
  });

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    sections={[{
      type: "regular",
      key: "freeFieldDefinition",
      title: RESX.FreeField.resxFreeField,
      fields: [{
        key: "category",
        label: RESX.FreeField.Category,
        render: () => <div>{freeFieldDefinition.category.title}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.FreeFieldCategoryForList>) => <FreeFieldCategoryDropdown
            value={value}
            onChange={(v) => onChange(v, true)}
            mainArea={props.mainArea}
            onEsc={() => cancel()}
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: freeFieldDefinition.label,
          submitChange: (category: server.dto.FreeFieldCategoryForList) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, category }),
          ),
        }
      }, {
        key: "type",
        label: RESX.FreeField.FieldType,
        render: () => <div>{freeFieldDefinition.type.text}</div>,
        editDisabled: !canModifyFieldType(freeFieldDefinition.type.value),
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<server.dto.FormElementType>) => <div className="main-section-template-fields-padding">
            <FreeFieldModifyRadio
              value={value.value}
              onChange={(v) => onChange(getFormElementType(v), true)}
            />
          </div>,
          fullWidth: true,
          type: "custom",
          initialValue: freeFieldDefinition.type,
          submitChange: (type: server.dto.FormElementType) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, type }),
          ),
        }
      }, {
        key: "label",
        label: RESX.GeneralLabels.Label,
        render: () => <TranslationValueReadonly values={freeFieldDefinition.label} />,
        inPageEdit: {
          render: ({ value, onChange, submit, isSaving }: RenderEditOptions<server.dto.TranslationValue[]>) => <RequiredTranslationValueInput
            submit={submit}
            onChange={onChange}
            values={value}
            isSaving={isSaving}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: freeFieldDefinition.label,
          submitChange: (label: server.dto.TranslationValue[]) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, label }),
          ),
        }
      }, {
        key: "subLabel",
        label: RESX.GeneralLabels.LabelSubscript,
        isEmpty: freeFieldDefinition.subLabel.length === 0,
        render: () => <TranslationValueReadonly values={freeFieldDefinition.subLabel} />,
        inPageEdit: {
          render: ({ value, onChange, submit, isSaving }: RenderEditOptions<server.dto.TranslationValue[]>) => <OptionalTranslationValueInput
            submit={submit}
            onChange={onChange}
            values={value}
            isSaving={isSaving}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: freeFieldDefinition.subLabel,
          submitChange: (subLabel: server.dto.TranslationValue[]) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, subLabel }),
          ),
        }
      }, {
        key: "configuration",
        label: RESX.GeneralLabels.Configuration,
        hideLabel: true,
        render: () => <div>
          <h3>{RESX.GeneralLabels.Configuration}</h3>
        </div>
      }, {
        key: "reference",
        label: RESX.FreeField.ReferenceCode,
        sublabel: RESX.FreeField.ReferenceCodeDescription,
        render: () => <div>FF{freeFieldDefinition.reference}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <FreeFieldReferenceInput
            mainArea={props.mainArea}
            focusOnMount
            onEnter={() => submit()}
            onEsc={() => cancel()}
            validatorKey="reference"
            value={value}
            onChange={onChange}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: freeFieldDefinition.reference,
          submitChange: (reference: string) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, reference }),
          ),
        }
      }, {
        key: "visibility",
        label: RESX.GeneralLabels.Visible,
        render: () => <>
          <div className="flex gap">
            {freeFieldDefinition.hiddenFromItinerary ? <i className="icon-check green" /> : <i className="icon-x red" />}
            <span>{RESX.FreeField.HideFromItinerary}</span>
          </div>
          <div className="flex gap">
            {freeFieldDefinition.hiddenFromMobileApp ? <i className="icon-check green" /> : <i className="icon-x red" />}
            <span>{RESX.FreeField.HideFromMobileApp}</span>
          </div>
        </>,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<{ hiddenFromItinerary: boolean, hiddenFromMobileApp: boolean }>) => <div className="main-section-template-fields-padding flex-column gap-small">
            <Checkbox
              checked={value.hiddenFromItinerary}
              label={RESX.FreeField.HideFromItinerary}
              onChange={(_, hiddenFromItinerary) => onChange({ ...value, hiddenFromItinerary })}
            />
            <Checkbox
              checked={value.hiddenFromMobileApp}
              label={RESX.FreeField.HideFromMobileApp}
              onChange={(_, hiddenFromMobileApp) => onChange({ ...value, hiddenFromMobileApp })}
            />
          </div>,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: { hiddenFromItinerary: freeFieldDefinition.hiddenFromItinerary, hiddenFromMobileApp: freeFieldDefinition.hiddenFromMobileApp },
          submitChange: (visibilty: { hiddenFromItinerary: boolean, hiddenFromMobileApp: boolean }) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({
              ...freeFieldDefinition,
              hiddenFromItinerary: visibilty.hiddenFromItinerary,
              hiddenFromMobileApp: visibilty.hiddenFromMobileApp
            }),
          ),
        }
      }, {
        key: "defaultNumberWithDecimal",
        label: RESX.FreeField.DefaultValue,
        isEmpty: freeFieldDefinition.defaultNumberWithDecimal === null,
        hidden: freeFieldDefinition.type.value !== "decimal",
        render: () => <div>{freeFieldDefinition.defaultNumberWithDecimal}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number | null>) => <OptionalNumberInput
            canBeNegative
            canBeEmpty
            autoFocus
            width={140}
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={2}
          />,
          fullWidth: false,
          type: "confirmOrCancelButton",
          initialValue: freeFieldDefinition.defaultNumberWithDecimal,
          submitChange: (defaultNumberWithDecimal: number | null) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, defaultNumberWithDecimal }),
          ),
        }
      }, {
        key: "defaultNumberWithoutDecimal",
        label: RESX.FreeField.DefaultValue,
        isEmpty: freeFieldDefinition.defaultNumberWithoutDecimal === null,
        hidden: freeFieldDefinition.type.value !== "integer",
        render: () => <div>{freeFieldDefinition.defaultNumberWithoutDecimal}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number | null>) => <OptionalNumberInput
            canBeNegative
            canBeEmpty
            autoFocus
            width={140}
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={0}
          />,
          fullWidth: false,
          type: "confirmOrCancelButton",
          initialValue: freeFieldDefinition.defaultNumberWithoutDecimal,
          submitChange: (defaultNumberWithoutDecimal: number | null) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, defaultNumberWithoutDecimal }),
          ),
        }
      }, {
        key: "optionsLabel",
        label: RESX.FreeField.DropdownOptions,
        hideLabel: true,
        hidden: freeFieldDefinition.type.value !== "dropdown",
        render: () => <div>
          <h3>{RESX.FreeField.DropdownOptions}</h3>
        </div>
      }, {
        key: "options",
        label: RESX.Status.Options,
        hidden: freeFieldDefinition.type.value !== "dropdown",
        isEmpty: freeFieldDefinition.choices.length === 0,
        render: () => <DropdownOptionsDetail
          options={freeFieldDefinition.choices}
          defaultOption={freeFieldDefinition.defaultText}
        />,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<{ options: string[], defaultOption: string | null }>) => <div className="main-section-template-fields-padding">
            <DropdownOptionsInPageEdit
              onSave={(options, defaultOption) => onChange({ options, defaultOption }, true)}
              onClose={() => cancel()}
              defaultOption={value.defaultOption}
              options={value.options}
            />
          </div>,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: { options: freeFieldDefinition.choices, defaultOption: freeFieldDefinition.defaultText },
          submitChange: (value: { options: string[], defaultOption: string | null }) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({
              ...freeFieldDefinition,
              choices: value.options,
              defaultText: value.defaultOption
            }),
          ),
        }
      }, {
        key: "defaultTextSingleLine",
        label: RESX.FreeField.DefaultValue,
        isEmpty: !freeFieldDefinition.defaultText,
        hidden: freeFieldDefinition.type.value !== "textSingleLine",
        render: () => <div>{freeFieldDefinition.defaultText}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
            focusOnMount
            maxLength={serverReference.constants.freeFieldDefinition.maxDefaultTextgLength}
            value={value}
            onChange={(v) => { onChange(v); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: freeFieldDefinition.defaultText,
          submitChange: (defaultText: string) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, defaultText }),
          ),
        }
      }, {
        key: "defaultTextMultiLine",
        label: RESX.FreeField.DefaultValue,
        isEmpty: !freeFieldDefinition.defaultText,
        hidden: freeFieldDefinition.type.value !== "textMultiLine",
        render: () => <OptionalTextAreaDetails>{freeFieldDefinition.defaultText}</OptionalTextAreaDetails>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
            focusOnMount
            maxLength={serverReference.constants.freeFieldDefinition.maxDefaultTextgLength}
            value={value}
            onChange={(v) => { onChange(v); }}
            onCtrlEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: freeFieldDefinition.defaultText,
          submitChange: (defaultText: string) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, defaultText }),
          ),
        }
      }, {
        key: "defaultPercentage",
        label: RESX.FreeField.DefaultValue,
        isEmpty: Number.isNaN(freeFieldDefinition.defaultPercentage),
        hidden: freeFieldDefinition.type.value !== "percentage",
        render: () => <ReadonlyPercentage value={`${freeFieldDefinition.defaultPercentage * 100}`} />,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <PercentageInput
            autoFocus
            onEnter={v => onChange(v, true)}
            onEsc={cancel}
            canBeNegative={false}
            digitsAfterDecimalPoint={2}
            enablePercentageLabel
            value={value || 0}
            onChange={v => onChange(v)}
          />,
          fullWidth: false,
          type: "confirmOrCancelButton",
          initialValue: freeFieldDefinition.defaultPercentage,
          submitChange: (defaultPercentage: number | null) => window.CoreApi.FreeFieldDefinition.UpdateDetails(
            getUpdateFreeFieldDefinitionInstruction({ ...freeFieldDefinition, defaultPercentage }),
          ),
        }
      }]
    }, {
      type: "collapsable",
      key: "freeFieldProfiles",
      title: RESX.FreeField.PermissionProfiles,
      fields: [{
        key: "permissionProfiles",
        label: RESX.FreeField.PermissionProfile,
        hideLabel: true,
        render: () => <FreeFieldPermissions
          readonly
          permissions={freeFieldDefinition.permissions}
          onChange={() => { }}
        />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<server.dto.FreeFieldProfileWithPermission[]>) => <div className="main-section-template-fields-padding">
            <FreeFieldPermissions
              permissions={value}
              onChange={(permissions) => { onChange(permissions); }}
            />
          </div>,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: freeFieldDefinition.permissions,
          submitChange: (permissions: server.dto.FreeFieldProfileWithPermission[]) => window.CoreApi.FreeFieldDefinition.AssignProfileLevelsToFreeFieldDefinition({
            freeFieldDefinitionId: props.freeFieldDefinition.freeFieldDefinitionId,
            profileLevel: permissions.map(p => ({ freeFieldProfileId: p.freeFieldProfileId, level: p.level })),
          }),
        }
      }]
    }]}
  />
}

const getDefaultNumberWithDecimal = (ffd: server.dto.FreeFieldDefinition): number | null => {
  if (ffd.type === "decimal") {
    return parseFloat(ffd.defaultValue);
  }

  return null;
}

const getDefaultNumberWithoutDecimal = (ffd: server.dto.FreeFieldDefinition): number | null => {
  if (ffd.type === "integer") {
    return parseInt(ffd.defaultValue);
  }

  return null;
}

const getDefaultText = (ffd: server.dto.FreeFieldDefinition): string => {
  if (ffd.type === "textSingleLine" || ffd.type === "textMultiLine" || ffd.type === "dropdown") {
    return ffd.defaultValue;
  }

  return null;
}

const getDefaultPercentage = (ffd: server.dto.FreeFieldDefinition): number | null => {
  if (ffd.type === "percentage") {
    return parseFloat(ffd.defaultValue) / 100;
  }
}